import { FINANCIAL_TRANSACTIONS } from '../../constants/accountTransactionConstants';
import {
  getFinancialPortfolioPendingTransactions,
  getPortfolioTransactionSingle,
} from '../../request/portfolioRequests';
import {
  SINGLE_TRANSACTION_TYPE_TRIGGER_ORDER,
  triggerOrderStatusTypesArray,
} from '../enum/api/transactionTypes';
import { parseEnumType } from './enumMappers';
import {
  mergeTriggerOrderDetails,
  normalizeSingleTransaction,
} from './transcation/singleTransactionHelper';

// /portfolio/financial-balances?triggerOrderUid=<uid>&accountUid=<uid>&source=email
// Case is ignored: the BE has deployed lowercase params for other portfolio
// deep links (see SfhTransactionDeepLink). `source` rides along but nothing
// reads it; it is only removed from the URL with the rest.
const DEEP_LINK_PARAMS = ['triggerorderuid', 'accountuid', 'source'];

const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const readParam = (params, name) => {
  const key = Array.from(params.keys()).find(
    (paramKey) => paramKey.toLowerCase() === name,
  );

  return key ? params.get(key) : null;
};

// The deep link a URL search string carries, or null when it isn't one. Both
// uids must be GUIDs: they are put into the request URLs below.
export const parseTriggerOrderDeepLink = (search) => {
  const params = new URLSearchParams(search);
  const triggerOrderUid = readParam(params, 'triggerorderuid');
  const accountUid = readParam(params, 'accountuid');

  if (!GUID.test(triggerOrderUid) || !GUID.test(accountUid)) {
    return null;
  }

  return { triggerOrderUid, accountUid };
};

// The search string without the deep link's params, so a refresh or Back
// doesn't reopen the modal. Any other param stays.
export const removeTriggerOrderDeepLinkParams = (search) => {
  const params = new URLSearchParams(search);

  Array.from(params.keys())
    .filter((key) => DEEP_LINK_PARAMS.includes(key.toLowerCase()))
    .forEach((key) => params.delete(key));

  return params.toString();
};

// Both calls opt out of the redirect to the error page on a 500, which would
// replace the portfolio the user landed on (the same flag the details saga sets).
const REQUEST_OPTIONS = { skipInternalServerErrorRedirect: true };

const fetchPendingRow = ({ accountUid, triggerOrderUid }) =>
  getFinancialPortfolioPendingTransactions(
    accountUid,
    FINANCIAL_TRANSACTIONS,
    REQUEST_OPTIONS,
  )
    .then(({ data }) =>
      Array.isArray(data)
        ? data.find(
            (row) =>
              String(row.TriggerOrderUid).toLowerCase() ===
              triggerOrderUid.toLowerCase(),
          )
        : undefined,
    )
    .catch(() => undefined);

const fetchDetails = ({ accountUid, triggerOrderUid }) =>
  getPortfolioTransactionSingle(
    accountUid,
    triggerOrderUid,
    SINGLE_TRANSACTION_TYPE_TRIGGER_ORDER,
    REQUEST_OPTIONS,
  )
    .then(({ data }) => normalizeSingleTransaction(data))
    .catch(() => null);

// What TriggerOrderDetailsModal needs to show one order, or null when it
// can't be loaded (not the account's order, or the calls failed). Opens the
// order the way the transaction tables do:
//   - a row still in the pending list: the row, with an Active order's details
//     merged in (the other statuses open on the row alone);
//   - no row: the order has ended (canceled or expired), and its details are
//     the whole modal (isEnded), as in Recent Transactions.
// Requests go straight to the API for the link's own account: the pending list
// in the store is shared, replaced by whichever fetch lands last, and is
// unreliable for as long as the account is still switching.
export const loadTriggerOrderForDeepLink = (deepLink) =>
  fetchPendingRow(deepLink).then((row) => {
    if (!row) {
      return fetchDetails(deepLink).then((details) =>
        details ? { transaction: details, isEnded: true } : null,
      );
    }

    const status = parseEnumType(
      triggerOrderStatusTypesArray,
      row.TriggerOrderStatus,
    );

    if (status !== 'Active') {
      return { transaction: row, isEnded: false };
    }

    return fetchDetails(deepLink).then((details) => ({
      transaction: details ? mergeTriggerOrderDetails(details, row) : row,
      isEnded: false,
    }));
  });
